import { useState, useEffect, useCallback } from 'react';
import { Fragment } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, CheckCircle, Trash2, Search as SearchIcon, ChevronDown, ChevronRight, Bug, Users, Send, Wand2, Save, Clock, Mail } from 'lucide-react';
import { fmtDate } from '@/lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DiagnosticaErroriPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState('168'); // 7 giorni default
  const [userEmailFilter, setUserEmailFilter] = useState('');
  const [search, setSearch] = useState('');
  const [includeResolved, setIncludeResolved] = useState(false);
  const [expanded, setExpanded] = useState({}); // id → bool

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/client-errors`, {
        params: {
          hours: parseInt(hours, 10) || 168,
          limit: 300,
          user_email: userEmailFilter.trim(),
          search: search.trim(),
          include_resolved: includeResolved,
        },
        withCredentials: true,
      });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento diagnostica');
    } finally {
      setLoading(false);
    }
  }, [hours, userEmailFilter, search, includeResolved]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const resolveErr = async (id) => {
    try {
      await axios.put(`${API}/admin/client-errors/${id}/resolve`, {}, { withCredentials: true });
      toast.success('Errore segnato come risolto');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const deleteErr = async (id) => {
    if (!window.confirm('Cancellare definitivamente questo errore?')) return;
    try {
      await axios.delete(`${API}/admin/client-errors/${id}`, { withCredentials: true });
      toast.success('Errore cancellato');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const purgeResolved = async () => {
    if (!window.confirm('Cancellare TUTTI gli errori risolti più vecchi di 30 giorni?')) return;
    try {
      const r = await axios.post(`${API}/admin/client-errors/purge`, {}, { params: { older_than_hours: 720 }, withCredentials: true });
      toast.success(`${r.data?.deleted || 0} errori cancellati`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  // iter92: test email Resend
  const [testEmailBusy, setTestEmailBusy] = useState(false);
  const sendTestEmail = async () => {
    const to = window.prompt('Invia email di test a:', 'admin@cantieri.com');
    if (!to || !to.includes('@')) return;
    setTestEmailBusy(true);
    try {
      const r = await axios.post(`${API}/admin/test-email`, { to }, { withCredentials: true });
      toast.success(`Email di test inviata a ${r.data?.sent_to} (da ${r.data?.from})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invio fallito');
    } finally {
      setTestEmailBusy(false);
    }
  };

  // iter93: analizza e risolvi automaticamente
  const [autoRepairBusy, setAutoRepairBusy] = useState(false);
  const autoRepair = async () => {
    setAutoRepairBusy(true);
    try {
      const r = await axios.post(`${API}/admin/client-errors/auto-repair`, {}, {
        params: { hours: parseInt(hours, 10) || 168 },
        withCredentials: true,
      });
      const d = r.data || {};
      toast.success(d.message || `${d.resolved || 0} errori risolti automaticamente`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore analisi');
    } finally {
      setAutoRepairBusy(false);
    }
  };

  // Colori per severity (usati dal badge categoria)
  const severityStyle = (sev) => {
    if (sev === 'error') return 'bg-red-100 text-red-800 border-red-200';
    if (sev === 'warning') return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  // iter94: settaggi notifiche (email destinatario + toggle pulizia notturna)
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [crashAlertEmail, setCrashAlertEmail] = useState('');
  const [nightlyEnabled, setNightlyEnabled] = useState(true);
  const [currentRecipients, setCurrentRecipients] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/settings/crash-alert`, { withCredentials: true });
        setCrashAlertEmail(r.data?.crash_alert_email || '');
        setNightlyEnabled(r.data?.nightly_auto_repair_enabled !== false);
        setCurrentRecipients(r.data?.current_recipients || []);
      } catch (err) {
        // non-blocking
      } finally {
        setSettingsLoaded(true);
      }
    })();
  }, []);

  const saveSettings = async () => {
    setSettingsBusy(true);
    try {
      const r = await axios.put(`${API}/admin/settings/crash-alert`, {
        crash_alert_email: crashAlertEmail.trim(),
        nightly_auto_repair_enabled: nightlyEnabled,
      }, { withCredentials: true });
      setCurrentRecipients(r.data?.current_recipients || []);
      toast.success('Impostazioni salvate');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSettingsBusy(false);
    }
  };

  const pageFromUrl = (loc) => {
    try {
      const u = new URL(String(loc || ''));
      return u.pathname + (u.hash || '');
    } catch { return String(loc || '-'); }
  };

  const shortErrMsg = (msg) => {
    const s = String(msg || '');
    return s.length > 220 ? s.slice(0, 220) + '…' : s;
  };

  // Raggruppa per utente per KPI
  const uniqueUsers = new Set(items.map((i) => i.user_email).filter(Boolean));
  const uniquePages = new Set(items.map((i) => pageFromUrl(i.location)));

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="diagnostica-errori-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Bug className="h-5 w-5 text-red-500" />Diagnostica Errori Live
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Crash JavaScript degli utenti dell&apos;app, catturati in tempo reale dai boundary React</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={autoRepair} disabled={autoRepairBusy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="diag-auto-repair" title="Classifica ogni errore in italiano leggibile e risolve automaticamente quelli transitori (SW, rete, sessione scaduta, ecc)">
            <Wand2 className="h-4 w-4 mr-1.5" />{autoRepairBusy ? 'Analisi in corso...' : 'Analizza e risolvi'}
          </Button>
          <Button variant="outline" size="sm" onClick={sendTestEmail} disabled={testEmailBusy} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="diag-test-email" title="Verifica configurazione Resend inviando un'email di test">
            <Send className="h-4 w-4 mr-1.5" />{testEmailBusy ? 'Invio...' : 'Test email'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="diag-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" />Aggiorna
          </Button>
          <Button variant="outline" size="sm" onClick={purgeResolved} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="diag-purge">
            <Trash2 className="h-4 w-4 mr-1.5" />Svuota risolti (&gt;30gg)
          </Button>
        </div>
      </div>

      {/* iter94: Impostazioni notifiche + pulizia notturna */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-2 text-xs text-blue-900">
            <Wand2 className="h-4 w-4 mt-0.5 shrink-0 text-blue-700" />
            <div>
              <b>Analizza e risolvi:</b> traduce ogni errore in italiano e chiude quelli innocui (cache, rete, sessione).
              Restano aperti solo i veri bug.
              <br />
              <b>Notifiche push:</b> ricevi un&apos;email quando lo stesso utente ha 2+ crash in 1h (max 1 alert ogni 6h).
            </div>
          </div>

          {settingsLoaded && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-blue-200">
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs uppercase text-blue-900 font-semibold flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" />Email destinatario notifiche crash
                </Label>
                <Input
                  value={crashAlertEmail}
                  onChange={(e) => setCrashAlertEmail(e.target.value)}
                  placeholder="Lascia vuoto per inviare a tutti gli admin"
                  className="h-9 bg-white"
                  data-testid="setting-crash-alert-email"
                />
                <p className="text-[10px] text-blue-700">
                  Puoi inserire una o più email separate da virgola. <b>Attuali:</b> {currentRecipients.length ? currentRecipients.join(', ') : '(nessuno — email non inviate)'}
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase text-blue-900 font-semibold flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />Pulizia notturna
                </Label>
                <div className="flex items-center gap-2 bg-white border border-blue-200 rounded-md px-3 py-1.5 h-9">
                  <input
                    type="checkbox"
                    id="nightly-toggle"
                    checked={nightlyEnabled}
                    onChange={(e) => setNightlyEnabled(e.target.checked)}
                    className="h-4 w-4"
                    data-testid="setting-nightly-enabled"
                  />
                  <label htmlFor="nightly-toggle" className="text-sm text-slate-700 cursor-pointer">
                    {nightlyEnabled ? 'Attiva (03:00)' : 'Disattivata'}
                  </label>
                </div>
                <p className="text-[10px] text-blue-700">Analizza e risolve gli errori automaticamente ogni notte</p>
              </div>
              <div className="md:col-span-3 flex justify-end">
                <Button
                  size="sm"
                  onClick={saveSettings}
                  disabled={settingsBusy}
                  className="bg-blue-700 text-white hover:bg-blue-800"
                  data-testid="setting-save-btn"
                >
                  <Save className="h-3.5 w-3.5 mr-1.5" />{settingsBusy ? 'Salvataggio...' : 'Salva impostazioni'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase">Errori attivi</p>
            <p className="text-2xl font-bold text-red-600" data-testid="diag-kpi-total">{total}</p>
            <p className="text-[10px] text-slate-400 mt-1">nel periodo selezionato</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase">Utenti colpiti</p>
            <p className="text-2xl font-bold text-amber-600 flex items-center gap-1">
              <Users className="h-5 w-5" />{uniqueUsers.size}
            </p>
            <p className="text-[10px] text-slate-400 mt-1">email distinte</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase">Pagine con crash</p>
            <p className="text-2xl font-bold text-blue-600">{uniquePages.size}</p>
            <p className="text-[10px] text-slate-400 mt-1">route distinte</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase">Mostrati</p>
            <p className="text-2xl font-bold text-slate-900">{items.length}</p>
            <p className="text-[10px] text-slate-400 mt-1">limite {items.length >= 300 ? '300 (max)' : '300'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <Card className="border-slate-200">
        <CardContent className="p-3 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs uppercase text-slate-500">Periodo</Label>
            <Select value={hours} onValueChange={setHours}>
              <SelectTrigger className="h-9" data-testid="diag-filter-hours"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Ultima ora</SelectItem>
                <SelectItem value="24">Ultime 24 ore</SelectItem>
                <SelectItem value="168">Ultimi 7 giorni</SelectItem>
                <SelectItem value="720">Ultimi 30 giorni</SelectItem>
                <SelectItem value="2160">Ultimi 90 giorni</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-slate-500">Utente (email)</Label>
            <div className="relative">
              <Users className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={userEmailFilter}
                onChange={(e) => setUserEmailFilter(e.target.value)}
                placeholder="es. fra.milone"
                className="h-9 pl-8"
                data-testid="diag-filter-email"
              />
            </div>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs uppercase text-slate-500">Ricerca (messaggio / pagina / stack)</Label>
            <div className="relative">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="es. RangeError, /admin/ddt, undefined"
                className="h-9 pl-8"
                data-testid="diag-filter-search"
              />
            </div>
          </div>
          <div className="sm:col-span-4 flex items-center gap-2">
            <input
              id="include-resolved"
              type="checkbox"
              checked={includeResolved}
              onChange={(e) => setIncludeResolved(e.target.checked)}
              className="h-4 w-4"
              data-testid="diag-include-resolved"
            />
            <label htmlFor="include-resolved" className="text-sm text-slate-700 cursor-pointer">Mostra anche errori già risolti</label>
          </div>
        </CardContent>
      </Card>

      {/* Tabella errori */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-6"></TableHead>
                <TableHead className="text-xs uppercase">Data</TableHead>
                <TableHead className="text-xs uppercase">Utente</TableHead>
                <TableHead className="text-xs uppercase">Pagina</TableHead>
                <TableHead className="text-xs uppercase">Messaggio</TableHead>
                <TableHead className="text-xs uppercase text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">
                  <CheckCircle className="h-10 w-10 mx-auto text-emerald-400 mb-2" />
                  Nessun errore nel periodo — sistema in salute
                </TableCell></TableRow>
              ) : items.map((err) => (
                <Fragment key={err.id}>
                  <TableRow className="border-slate-100" data-testid={`diag-row-${err.id}`}>
                    <TableCell className="pl-3">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => toggleExpand(err.id)} data-testid={`diag-expand-${err.id}`}>
                        {expanded[err.id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </Button>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <div className="text-slate-700 font-medium">{fmtDate(err.created_at, 'dd-MM-yyyy')}</div>
                      <div className="text-slate-400">{fmtDate(err.created_at, 'HH:mm:ss')}</div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {err.user_email ? (
                        <span className="font-mono text-slate-700">{err.user_email}</span>
                      ) : (
                        <span className="text-slate-400 italic">anonimo</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      <span className="font-mono text-blue-700">{pageFromUrl(err.location)}</span>
                    </TableCell>
                    <TableCell className="text-xs max-w-md">
                      {err.category_label ? (
                        <div className="mb-1 flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className={severityStyle(err.severity)}>
                            {err.category_label}
                          </Badge>
                        </div>
                      ) : null}
                      {err.friendly_message ? (
                        <div className="text-slate-800 mb-1" data-testid={`diag-friendly-${err.id}`}>
                          {err.friendly_message}
                        </div>
                      ) : (
                        <div className="flex items-start gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                          <span className="text-slate-700 break-all">{shortErrMsg(err.message)}</span>
                        </div>
                      )}
                      {err.resolved && (
                        <Badge className="mt-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[9px]">
                          {String(err.resolved_by || '').startsWith('auto-repair') ? 'Risolto automaticamente' : 'Risolto'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!err.resolved && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:bg-emerald-50" onClick={() => resolveErr(err.id)} title="Segna come risolto" data-testid={`diag-resolve-${err.id}`}>
                            <CheckCircle className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={() => deleteErr(err.id)} title="Elimina" data-testid={`diag-delete-${err.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {expanded[err.id] && (
                    <TableRow className="bg-slate-50 border-slate-100">
                      <TableCell colSpan={6} className="py-3 px-6">
                        <div className="space-y-2 text-xs">
                          <div>
                            <span className="font-semibold text-slate-600">URL completo:</span>
                            <span className="ml-2 font-mono text-blue-700 break-all">{err.location || '-'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-slate-600">Timestamp originale:</span>
                            <span className="ml-2 font-mono text-slate-700">{err.timestamp || '-'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-slate-600">Browser:</span>
                            <span className="ml-2 text-slate-700 break-all">{err.user_agent || '-'}</span>
                          </div>
                          {err.message && (
                            <div>
                              <span className="font-semibold text-slate-600">Messaggio completo:</span>
                              <pre className="mt-1 p-2 bg-white border border-slate-200 rounded text-[11px] font-mono text-red-700 whitespace-pre-wrap break-all">{err.message}</pre>
                            </div>
                          )}
                          {err.stack && (
                            <div>
                              <span className="font-semibold text-slate-600">Stack trace:</span>
                              <pre className="mt-1 p-2 bg-white border border-slate-200 rounded text-[10px] font-mono text-slate-700 whitespace-pre-wrap break-all max-h-64 overflow-y-auto">{err.stack}</pre>
                            </div>
                          )}
                          {err.component_stack && (
                            <div>
                              <span className="font-semibold text-slate-600">Component stack (React):</span>
                              <pre className="mt-1 p-2 bg-white border border-slate-200 rounded text-[10px] font-mono text-slate-700 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">{err.component_stack}</pre>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
